import type {ArchivoDeExportacion, ArchivosDeExportacion, ExportacionDetalle} from '@cnc/contracts';
import {useMemo, useRef, useState} from 'react';
import {enlaceDeVistaPrevia, enlaceParaAbrir, useArchivosDeExportacion, useSubirADropbox} from '../api/carpetas';
import {ErrorDeApi} from '../api/cliente';
import {rutaDelZipDelExpediente, useTraerDeDropbox} from '../api/documentos';
import {tr} from '../i18n/idioma';
import {fecha, numero} from '../utilidades/formato';
import {CarpetaEnDropbox} from './carpeta-en-dropbox';
import {Expediente} from './expediente';
import {useNivel} from './sesion-contexto';
import {VistaPrevia} from './vista-previa';
import {PistaDeSoltar, ZonaParaSoltar} from './zona-para-soltar';

const tamano = (bytes: number) =>
  bytes >= 1024 * 1024 ? `${numero(bytes / 1024 / 1024, 1)} MB` : `${numero(Math.max(1, Math.round(bytes / 1024)))} KB`;

const madreDe = (ruta: string) => (ruta.includes('/') ? ruta.slice(0, ruta.lastIndexOf('/')) : '');
const nombreDe = (ruta: string) => ruta.slice(ruta.lastIndexOf('/') + 1);

// Archivos y expediente de la exportacion en una sola vista (decisiones 0085 y
// 0092): la carpeta de Dropbox para navegarla como en Dropbox, con la lista de
// chequeo (✓ estan / ✗ faltan), la categoria de cada archivo, si ya esta en
// el expediente y su vista previa. Debajo, solo lo del expediente que no esta
// en Dropbox. Sin Dropbox, el expediente de siempre.
export function DocumentosDeLaExportacion({
  codigo,
  documentos,
}: {
  codigo: string;
  documentos: ExportacionDetalle['documentos'];
}) {
  const consulta = useArchivosDeExportacion(codigo);
  const d = consulta.data;
  if (!d || !d.disponible)
    return (
      <>
        <CarpetaEnDropbox codigo={codigo} />
        {d?.error && <p className="text-sm text-alerta">{d.error}</p>}
        <Expediente codigo={codigo} documentos={documentos} />
      </>
    );
  const enDropbox = new Set(d.archivos.map(a => `${a.nombre.toLowerCase()}|${a.bytes}`));
  const soloEnLaApp = documentos.filter(x => !enDropbox.has(`${x.nombre.toLowerCase()}|${x.tamanoBytes}`));
  return (
    <>
      <Explorador codigo={codigo} datos={d} recargar={() => void consulta.refetch()} cargando={consulta.isFetching} />
      <Expediente codigo={codigo} documentos={soloEnLaApp} soloEnLaApp total={documentos.length} />
    </>
  );
}

export function Explorador({
  codigo,
  datos,
  recargar,
  cargando,
  raiz,
  area = 'exportaciones.documentos',
}: {
  codigo: string;
  datos: ArchivosDeExportacion;
  recargar: () => void;
  cargando: boolean;
  // Concentrados por defecto; doré pasa sus rutas y su seccion (decision 0095).
  raiz?: string;
  area?: string;
}) {
  const [actual, setActual] = useState('');
  const [buscar, setBuscar] = useState('');
  const [resaltado, setResaltado] = useState<string | null>(null);
  const subir = useSubirADropbox(codigo, raiz);
  const entrada = useRef<HTMLInputElement>(null);
  const edita = useNivel(area) === 'editar';
  const [aviso, setAviso] = useState<string | null>(null);
  const [viendo, setViendo] = useState<ArchivoDeExportacion | null>(null);
  const traer = useTraerDeDropbox(codigo, raiz);
  const [traido, setTraido] = useState<string | null>(null);
  // Solo lo que "Traer al expediente" de verdad traeria.
  const fueraDelExpediente = datos.archivos.filter(a => a.porTraer).length;

  const traerAlExpediente = async () => {
    setTraido(null);
    try {
      const r = await traer.mutateAsync();
      setTraido(
        [
          tr(
            `${numero(r.nuevos)} archivos nuevos en el expediente${r.facturas ? `, ${numero(r.facturas)} facturas leídas` : ''}.`,
            `${numero(r.nuevos)} new files in the export file${r.facturas ? `, ${numero(r.facturas)} invoices read` : ''}.`,
          ),
          ...r.noTraidos.slice(0, 3).map(n => `${n.ruta}: ${n.motivo}`),
        ].join(' '),
      );
    } catch (e) {
      setTraido(e instanceof ErrorDeApi ? e.message : tr('No se pudo traer.', 'Could not bring them.'));
    }
  };

  const hijas = useMemo(() => datos.carpetas.filter(c => madreDe(c) === actual), [datos.carpetas, actual]);
  const cuantos = (carpeta: string) => datos.archivos.filter(a => a.ruta.startsWith(`${carpeta}/`)).length;
  const archivos: ArchivoDeExportacion[] = buscar.trim()
    ? datos.archivos.filter(a => a.nombre.toLowerCase().includes(buscar.trim().toLowerCase()))
    : datos.archivos.filter(a => madreDe(a.ruta) === actual);
  const obligatorios = datos.documentos.filter(x => x.obligatorio);
  const estan = obligatorios.filter(x => x.encontrados.length).length;
  const migas = actual ? actual.split('/') : [];

  const ir = (ruta: string, archivo?: string) => {
    setBuscar('');
    setActual(ruta);
    setResaltado(archivo ?? null);
  };

  const enviar = async (lista: File[]) => {
    if (!lista.length) return;
    setAviso(null);
    try {
      const r = await subir.mutateAsync({carpeta: actual, archivos: [...lista]});
      setAviso(
        [
          r.subidos.length ? tr(`Subidos: ${r.subidos.length}.`, `Uploaded: ${r.subidos.length}.`) : '',
          ...r.noSubidos.map(n => `${n.nombre}: ${n.motivo}`),
        ]
          .filter(Boolean)
          .join(' '),
      );
    } catch (e) {
      setAviso(e instanceof ErrorDeApi ? e.message : tr('No se pudo subir.', 'Could not upload.'));
    } finally {
      if (entrada.current) entrada.current.value = '';
    }
  };

  return (
    <section className="rounded-lg border border-linea bg-white p-5 text-sm">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-medium">{tr('Archivos de la exportación', 'Export files')}</h2>
          <p className="text-xs text-apagado">
            {tr(
              'La carpeta de Dropbox de la exportación y su expediente en una sola vista. Lo que se sube aquí queda en Dropbox, lo que se pone en Dropbox entra solo al expediente (cada 30 minutos o con "Traer al expediente") y los documentos que genera la app quedan en su subcarpeta.',
              'The export’s Dropbox folder and its export file in one view. What is uploaded here stays in Dropbox, what is put in Dropbox goes into the export file by itself (every 30 minutes or with "Bring into the export file") and the documents the app generates stay in their subfolder.',
            )}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {edita && (
            <button
              type="button"
              onClick={() => void traerAlExpediente()}
              disabled={traer.isPending}
              title={tr(
                'Pone en el expediente, con su categoría, lo que está en la carpeta y aún no está en el expediente. Las facturas en PDF se leen.',
                'Puts into the export file, with its category, what is in the folder and is not in the export file yet. PDF invoices are read.',
              )}
              className="rounded-md border border-linea px-3 py-1.5 hover:bg-fondo disabled:opacity-50"
            >
              {traer.isPending
                ? tr('Trayendo…', 'Bringing…')
                : tr(
                    `⇩ Traer al expediente${fueraDelExpediente ? ` (${numero(fueraDelExpediente)})` : ''}`,
                    `⇩ Bring into the export file${fueraDelExpediente ? ` (${numero(fueraDelExpediente)})` : ''}`,
                  )}
            </button>
          )}
          <a
            href={rutaDelZipDelExpediente(codigo, raiz)}
            className="rounded-md border border-linea px-3 py-1.5 hover:bg-fondo"
            download
          >
            {tr('↓ Expediente (ZIP)', '↓ Export file (ZIP)')}
          </a>
          {datos.carpeta.enlace && (
            <a
              href={datos.carpeta.enlace}
              target="_blank"
              rel="noreferrer"
              className="rounded-md border border-linea px-3 py-1.5 hover:bg-fondo"
            >
              {tr('Abrir en Dropbox ↗', 'Open in Dropbox ↗')}
            </a>
          )}
          <button
            type="button"
            onClick={recargar}
            disabled={cargando}
            className="rounded-md border border-linea px-3 py-1.5 hover:bg-fondo disabled:opacity-50"
          >
            {cargando ? tr('Leyendo…', 'Reading…') : tr('↻ Actualizar', '↻ Refresh')}
          </button>
        </div>
      </div>

      {traido && (
        <p className="mt-2 text-xs" role="status">
          {traido}
        </p>
      )}

      <div className="mt-4 grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,2fr)]">
        <div>
          <h3 className="font-medium">
            {tr('Documentos obligatorios', 'Required documents')}{' '}
            <span className={estan === obligatorios.length ? 'text-verde-oscuro' : 'text-alerta'}>
              {tr(`${estan} de ${obligatorios.length}`, `${estan} of ${obligatorios.length}`)}
            </span>
          </h3>
          {datos.documentos.length === 0 ? (
            <p className="mt-1 text-xs text-apagado">
              {tr(
                'Este tipo de exportación no tiene lista de chequeo: se arma en Administración → Carpetas en Dropbox.',
                'This kind of export has no checklist: it is set up in Administration → Dropbox folders.',
              )}
            </p>
          ) : (
            <ul className="mt-2 space-y-1">
              {datos.documentos.map(x => {
                const primero = x.encontrados[0];
                return (
                  <li key={x.nombre} className="flex items-start gap-2">
                    <span
                      className={
                        x.encontrados.length ? 'text-verde-oscuro' : x.obligatorio ? 'text-alerta' : 'text-apagado'
                      }
                    >
                      {x.encontrados.length ? '✓' : '✗'}
                    </span>
                    <span className="min-w-0">
                      {primero ? (
                        <button
                          type="button"
                          className="text-left hover:underline"
                          onClick={() => ir(madreDe(primero), primero)}
                          title={x.encontrados.join('\n')}
                        >
                          {x.nombre}
                          {x.encontrados.length > 1 ? ` (${x.encontrados.length})` : ''}
                        </button>
                      ) : (
                        <>
                          {x.nombre}
                          {!x.obligatorio && <span className="text-apagado">{tr(' (opcional)', ' (optional)')}</span>}
                          {x.subcarpeta && (
                            <button
                              type="button"
                              className="block text-xs text-apagado hover:underline"
                              onClick={() => ir(datos.carpetas.includes(x.subcarpeta!) ? x.subcarpeta! : '')}
                            >
                              {tr(`Va en ${x.subcarpeta}`, `Goes in ${x.subcarpeta}`)}
                            </button>
                          )}
                        </>
                      )}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <nav className="flex min-w-0 flex-wrap items-center gap-1">
              <button type="button" className="hover:underline" onClick={() => ir('')}>
                {datos.carpeta.ruta?.split('/').pop() ?? codigo}
              </button>
              {migas.map((m, i) => (
                <span key={`${i}-${m}`} className="flex items-center gap-1">
                  <span className="text-apagado">/</span>
                  <button type="button" className="hover:underline" onClick={() => ir(migas.slice(0, i + 1).join('/'))}>
                    {m}
                  </button>
                </span>
              ))}
            </nav>
            <input
              value={buscar}
              onChange={e => setBuscar(e.target.value)}
              placeholder={tr('Buscar en toda la carpeta', 'Search the whole folder')}
              className="ml-auto w-56 rounded-md border border-linea px-2 py-1 text-sm"
            />
          </div>

          {/* Lo que se suelta sobre la lista entra a la carpeta que se está viendo. */}
          <ZonaParaSoltar multiple deshabilitado={!edita || !!buscar.trim() || subir.isPending} onSoltar={enviar}>
            <ul className="mt-2 divide-y divide-linea rounded-md border border-linea">
              {!buscar.trim() &&
                hijas.map(c => (
                  <li key={c}>
                    <button
                      type="button"
                      onClick={() => ir(c)}
                      className="flex w-full items-center justify-between px-3 py-2 text-left hover:bg-fondo"
                    >
                      <span>📁 {nombreDe(c)}</span>
                      <span className="text-xs text-apagado">
                        {tr(`${cuantos(c)} archivos`, `${cuantos(c)} files`)}
                      </span>
                    </button>
                  </li>
                ))}
              {archivos.map(a => (
                <li
                  key={a.ruta}
                  className={`flex items-center gap-2 px-3 py-2 ${a.ruta === resaltado || a.ruta === viendo?.ruta ? 'bg-fondo' : ''}`}
                >
                  <a
                    href={enlaceParaAbrir(codigo, a.ruta, raiz)}
                    target="_blank"
                    rel="noreferrer"
                    className="min-w-0 flex-1 break-all hover:underline"
                    title={tr('Abrir en Dropbox', 'Open in Dropbox')}
                  >
                    📄 {buscar.trim() ? a.ruta : a.nombre}
                  </a>
                  {a.categoria && (
                    <span className="shrink-0 rounded bg-papel px-1.5 py-0.5 text-[10px] text-apagado">
                      {a.categoria}
                    </span>
                  )}
                  <EnLaApp archivo={a} />
                  <span className="shrink-0 text-xs text-apagado">
                    {tamano(a.bytes)}
                    {a.modificadoEl ? ` · ${fecha(a.modificadoEl.slice(0, 10))}` : ''}
                  </span>
                  {a.vistaPrevia && (
                    <button
                      type="button"
                      onClick={() => setViendo(viendo?.ruta === a.ruta ? null : a)}
                      aria-label={tr(`Ver ${a.nombre}`, `View ${a.nombre}`)}
                      className="shrink-0 rounded-md border border-linea px-2 py-0.5 text-xs hover:bg-white"
                    >
                      {tr('👁 Ver', '👁 View')}
                    </button>
                  )}
                </li>
              ))}
              {!archivos.length && (buscar.trim() || !hijas.length) && (
                <li className="px-3 py-2 text-apagado">
                  {buscar.trim()
                    ? tr('Nada con ese nombre.', 'Nothing with that name.')
                    : tr('Carpeta vacía.', 'Empty folder.')}
                </li>
              )}
            </ul>

            {edita && !buscar.trim() && (
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <input
                  ref={entrada}
                  type="file"
                  multiple
                  className="hidden"
                  onChange={e => {
                    void enviar([...(e.target.files ?? [])]);
                    e.target.value = '';
                  }}
                />
                <button
                  type="button"
                  onClick={() => entrada.current?.click()}
                  disabled={subir.isPending}
                  className="rounded-md border border-linea px-3 py-1.5 hover:bg-fondo disabled:opacity-50"
                >
                  {subir.isPending
                    ? tr('Subiendo…', 'Uploading…')
                    : tr(
                        `⇪ Subir aquí (${actual || 'carpeta principal'})`,
                        `⇪ Upload here (${actual || 'main folder'})`,
                      )}
                </button>
                {!subir.isPending && <PistaDeSoltar multiple />}
                {aviso && <span>{aviso}</span>}
              </div>
            )}
          </ZonaParaSoltar>
        </div>
      </div>

      {viendo && (
        <VistaPreviaDelArchivo codigo={codigo} archivo={viendo} raiz={raiz} onCerrar={() => setViendo(null)} />
      )}
    </section>
  );
}

// Que sabe la app del archivo: en el expediente, generado por la app, borrado
// del expediente (no se vuelve a traer) o pendiente de traer.
function EnLaApp({archivo: a}: {archivo: ArchivoDeExportacion}) {
  const [texto, ayuda, clase] =
    a.enLaApp === 'expediente'
      ? [
          tr('✓ expediente', '✓ export file'),
          tr('Ya está en el expediente', 'Already in the export file'),
          'text-verde-oscuro',
        ]
      : a.enLaApp === 'generado'
        ? [
            tr('generado en la app', 'generated by the app'),
            tr(
              'Lo generó la app: está en su paquete documental',
              'Generated by the app: it is in its document package',
            ),
            'text-apagado',
          ]
        : a.enLaApp === 'descartado'
          ? [
              tr('borrado del expediente', 'deleted from the export file'),
              tr(
                'Alguien lo borró del expediente: no se vuelve a traer',
                'Someone deleted it from the export file: it is not brought in again',
              ),
              'text-apagado',
            ]
          : a.porTraer
            ? [
                tr('por traer', 'to bring in'),
                tr('Aún no está en el expediente', 'Not in the export file yet'),
                'text-dorado',
              ]
            : [
                '·',
                tr('El expediente no guarda este tipo de archivo', 'The export file does not keep this kind of file'),
                'text-apagado',
              ];
  return (
    <span className={`shrink-0 text-xs ${clase}`} title={ayuda}>
      {texto}
    </span>
  );
}

// El archivo dentro de la app, sin descargarlo: imagen, PDF (tambien Word y
// PowerPoint, que Dropbox convierte) o la tabla de un Excel, aislada.
function VistaPreviaDelArchivo({
  codigo,
  archivo,
  raiz,
  onCerrar,
}: {
  codigo: string;
  archivo: ArchivoDeExportacion;
  raiz?: string;
  onCerrar: () => void;
}) {
  return (
    <VistaPrevia
      src={enlaceDeVistaPrevia(codigo, archivo.ruta, raiz)}
      nombre={archivo.nombre}
      titulo={archivo.ruta}
      abrir={{href: enlaceParaAbrir(codigo, archivo.ruta, raiz), texto: tr('Abrir en Dropbox ↗', 'Open in Dropbox ↗')}}
      onCerrar={onCerrar}
    />
  );
}
